/**
 * BokBo Search Widget — Drop-in Shopify Search Replacement
 *
 * Pure vanilla JS + CSS. No dependencies.
 * Finds the native Shopify search input, intercepts keystrokes,
 * and shows an AI-powered search dropdown with product results.
 *
 * Installation:
 *   Add this script to your Shopify theme (before </body>):
 *   <script src="https://cdn.bokbo.com/widget/bokbo-search.js"></script>
 */

(function () {
  'use strict';

  // ── Resolve the current store's myshopify handle ──────────────────
  // Shopify exposes `Shopify.shop` on every storefront page as the full
  // myshopify domain (e.g. "1abf9b-6f.myshopify.com"). We strip the suffix
  // so the widget and the admin dashboard write/read the same Redis key.
  // Falls back to "rackhunt" for standalone/demo pages where Shopify global
  // isn't present.
  //
  // Note on BOKBO_CONFIG.storeId: the theme-level inline config may set
  // a `storeId` field, but it's NOT consulted here. The myshopify
  // handle is the backend's canonical key for per-store indexing; a
  // legacy theme config value ("rackhunt" vs "1abf9b-6f") would
  // silently point the widget at the wrong namespace. Failing visibly
  // on an unrecognized store beats quietly searching the wrong one.
  function resolveStoreId() {
    try {
      if (window.Shopify && typeof window.Shopify.shop === 'string' && window.Shopify.shop) {
        return window.Shopify.shop.replace(/\.myshopify\.com$/i, '').toLowerCase();
      }
    } catch (e) {}
    return 'rackhunt';
  }

  // ── Config (customize per store) ─────────────────────────────────
  var BOKBO = {
    apiUrl: 'https://bokbo.com/search',
    storeId: resolveStoreId(),
    accentColor: '#D63031',
    resultLimit: 8,
    debounceMs: 300,
    minChars: 3,
    showPoweredBy: true,
    searchEnabled: true,
    // Autocomplete dropdown gate — mirrored from DEFAULT_STORE_SETTINGS.
    // Merchants can flip this off to let BokBo power the results page
    // without rendering the as-you-type dropdown over the header input.
    dropdownEnabled: true,
  };

  // ── Settings fetch (30s in-memory cache, 2000ms timeout, shared) ──
  // Timeout is 2000ms (not 500ms) because the settings endpoint can be slow
  // on cold starts / under load. At 500ms a slow response would silently fall
  // back to defaults (searchEnabled:true) and the dropdown would ignore the
  // admin toggle. 2s is long enough to survive a cold start while still being
  // well within user-tolerable latency for the first dropdown open.
  var SETTINGS_CACHE_KEY = '__bokboSettingsCache';
  var SETTINGS_CACHE_TTL = 30 * 1000;
  var SETTINGS_TIMEOUT_MS = 2000;

  function getSettingsUrl() {
    return BOKBO.apiUrl.replace(/\/search\/?$/, '') +
      '/settings/' + encodeURIComponent(BOKBO.storeId);
  }

  function fetchSettings(cb) {
    var cache = window[SETTINGS_CACHE_KEY];
    if (cache && (Date.now() - cache.fetchedAt) < SETTINGS_CACHE_TTL) {
      return cb(cache.data);
    }

    var done = false;
    var xhr = new XMLHttpRequest();
    var timer = setTimeout(function () {
      if (done) return;
      done = true;
      try { xhr.abort(); } catch (_) {}
      console.warn('[BokBo] Settings fetch timed out (>2000ms) — using defaults');
      cb(null);
    }, SETTINGS_TIMEOUT_MS);

    try {
      xhr.open('GET', getSettingsUrl(), true);
    } catch (e) {
      clearTimeout(timer);
      done = true;
      return cb(null);
    }

    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4 || done) return;
      done = true;
      clearTimeout(timer);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var data = JSON.parse(xhr.responseText);
          var settings = (data && data.settings) || null;
          if (settings) {
            window[SETTINGS_CACHE_KEY] = { data: settings, fetchedAt: Date.now() };
            return cb(settings);
          }
        } catch (_) {}
      }
      console.warn('[BokBo] Settings fetch failed (status ' + xhr.status + ') — using defaults');
      cb(null);
    };

    xhr.send();
  }

  function applySettings(s) {
    if (!s) return;
    if (typeof s.searchEnabled   === 'boolean') BOKBO.searchEnabled   = s.searchEnabled;
    if (typeof s.dropdownEnabled === 'boolean') BOKBO.dropdownEnabled = s.dropdownEnabled;
    if (typeof s.showBranding    === 'boolean') BOKBO.showPoweredBy   = s.showBranding;
    if (typeof s.minSearchChars  === 'number')  BOKBO.minChars        = s.minSearchChars;
    if (typeof s.accentColor     === 'string' && /^#[0-9a-fA-F]{6}$/.test(s.accentColor)) {
      BOKBO.accentColor = s.accentColor;
      document.documentElement.style.setProperty('--bokbo-accent', s.accentColor);
    }
  }

  // ── Styles ───────────────────────────────────────────────────────
  var STYLES = '\
    /* Defensive overrides for the native Shopify search input.\
       Themes often set text-overflow:ellipsis, overflow:hidden,\
       or large padding-inline-end to reserve space for a clear/search\
       button — causing long queries to visually truncate as the caret\
       scrolls past the padded region. These rules neutralize the most\
       common culprits so BokBo-bound inputs keep all typed characters\
       visible regardless of theme. */\
    input[type="search"],\
    input[name="q"],\
    input[name="search"],\
    input[aria-label="Search"],\
    .search-input input,\
    .search__input,\
    #search-input,\
    #Search,\
    #Search-In-Modal,\
    form[action="/search"] input[type="text"],\
    form[action="/search"] input[type="search"] {\
      text-overflow: clip !important;\
      overflow: visible !important;\
      min-width: 0 !important;\
      max-width: none !important;\
    }\
    /* Ensure common search-input wrappers don\'t clip the input. */\
    form[action="/search"],\
    form[action="/search"] *,\
    .search-modal__form,\
    .search-modal__form *,\
    .search-input,\
    .search-input * {\
      min-width: 0;\
    }\
    /* Subtle "searching" indicator for as-you-type feedback.\
       Applied to the active <input> as the class `bokbo-searching` while\
       a debounced request is in flight. Pulses a soft accent-colored ring\
       via box-shadow so we don\'t have to hunt for the theme\'s search\
       icon — which varies wildly across themes. Removed as soon as\
       results arrive, on error, or when the dropdown hides. */\
    @keyframes bokbo-search-pulse {\
      0%, 100% { box-shadow: 0 0 0 0   var(--bokbo-accent, #D63031), inset 0 0 0 1px rgba(214,48,49,0.10); }\
      50%      { box-shadow: 0 0 0 3px rgba(214,48,49,0.18), inset 0 0 0 1px rgba(214,48,49,0.35); }\
    }\
    input.bokbo-searching {\
      animation: bokbo-search-pulse 1s ease-in-out infinite !important;\
      transition: box-shadow 0.15s ease-out;\
    }\
    .bokbo-dropdown {\
      position: absolute;\
      top: 100%;\
      left: 0;\
      right: 0;\
      /* Sits above most theme chrome (typical navbars are 100-10000)\
         but intentionally BELOW the BokBo quick-view modal + wishlist\
         toast (both z-index 99999) so a stray stuck-open dropdown can\
         never sit on top of a modal the shopper just opened. Was\
         999999 — dropped one order of magnitude for this reason. */\
      z-index: 99998;\
      background: #fff;\
      border: 1px solid #e2e2e2;\
      border-top: 2px solid var(--bokbo-accent, #D63031);\
      border-radius: 0 0 8px 8px;\
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);\
      max-height: 70vh;\
      overflow-y: auto;\
      /* Prevents an iOS "overscroll" at the end of the internal\
         result list from bleeding into a window scroll — without\
         this, the scroll-close listener would fire when a shopper\
         drags past the last result in the dropdown. */\
      overscroll-behavior: contain;\
      display: none;\
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;\
      -webkit-overflow-scrolling: touch;\
    }\
    .bokbo-dropdown.bokbo-visible {\
      display: block;\
    }\
    .bokbo-result {\
      display: flex;\
      align-items: center;\
      gap: 12px;\
      padding: 10px 14px;\
      text-decoration: none;\
      color: #1a1a1a;\
      border-bottom: 1px solid #f0f0f0;\
      transition: background 0.15s ease;\
    }\
    .bokbo-result:last-child {\
      border-bottom: none;\
    }\
    .bokbo-result:hover,\
    .bokbo-result:focus {\
      background: #fafafa;\
      outline: none;\
    }\
    .bokbo-result:focus {\
      background: #f5f5f5;\
    }\
    .bokbo-result-img {\
      width: 52px;\
      height: 52px;\
      object-fit: cover;\
      border-radius: 6px;\
      flex-shrink: 0;\
      background: #f5f5f5;\
    }\
    .bokbo-result-info {\
      flex: 1;\
      min-width: 0;\
      display: flex;\
      flex-direction: column;\
      gap: 2px;\
    }\
    .bokbo-result-title {\
      font-size: 14px;\
      font-weight: 500;\
      line-height: 1.3;\
      color: #1a1a1a;\
      white-space: nowrap;\
      overflow: hidden;\
      text-overflow: ellipsis;\
    }\
    .bokbo-result-meta {\
      font-size: 12px;\
      color: #777;\
    }\
    .bokbo-result-price {\
      font-size: 14px;\
      font-weight: 600;\
      color: var(--bokbo-accent, #D63031);\
      flex-shrink: 0;\
      white-space: nowrap;\
    }\
    .bokbo-loading {\
      padding: 20px;\
      text-align: center;\
      font-size: 13px;\
      color: #999;\
    }\
    @keyframes bokbo-shimmer {\
      0% { background-position: -200px 0; }\
      100% { background-position: 200px 0; }\
    }\
    .bokbo-skeleton-row {\
      display: flex;\
      align-items: center;\
      gap: 12px;\
      padding: 10px 14px;\
      border-bottom: 1px solid #f0f0f0;\
    }\
    .bokbo-skeleton-row:last-child {\
      border-bottom: none;\
    }\
    .bokbo-skeleton-img {\
      width: 60px;\
      height: 60px;\
      border-radius: 6px;\
      flex-shrink: 0;\
      background: linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%);\
      background-size: 400px 100%;\
      animation: bokbo-shimmer 1.4s ease-in-out infinite;\
    }\
    .bokbo-skeleton-lines {\
      flex: 1;\
      display: flex;\
      flex-direction: column;\
      gap: 8px;\
    }\
    .bokbo-skeleton-line {\
      height: 12px;\
      border-radius: 4px;\
      background: linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%);\
      background-size: 400px 100%;\
      animation: bokbo-shimmer 1.4s ease-in-out infinite;\
    }\
    .bokbo-skeleton-line--title {\
      width: 70%;\
    }\
    .bokbo-skeleton-line--price {\
      width: 40%;\
    }\
    .bokbo-still-searching {\
      padding: 8px 14px;\
      text-align: center;\
      font-size: 12px;\
      color: #999;\
      border-top: 1px solid #f0f0f0;\
    }\
    .bokbo-no-results {\
      padding: 20px;\
      text-align: center;\
      font-size: 13px;\
      color: #999;\
    }\
    .bokbo-footer {\
      padding: 8px 14px;\
      text-align: right;\
      font-size: 11px;\
      color: #bbb;\
      border-top: 1px solid #f0f0f0;\
    }\
    .bokbo-footer a {\
      color: #bbb;\
      text-decoration: none;\
    }\
    .bokbo-footer a:hover {\
      color: var(--bokbo-accent, #D63031);\
    }\
    .bokbo-wrap {\
      position: relative;\
    }\
    @media (max-width: 768px) {\
      .bokbo-dropdown {\
        position: fixed;\
        top: auto;\
        left: 0;\
        right: 0;\
        bottom: 0;\
        /* Cap to 60vh AND min() it against a fixed 420px so on\
           very tall phones (iPhone Pro Max, etc.) the sheet does\
           not balloon to half the screen. overflow-y:auto is\
           inherited from the base rule — results overflow\
           internally instead of pushing body content. */\
        max-height: min(60vh, 420px);\
        overflow-y: auto;\
        border-radius: 12px 12px 0 0;\
        border: none;\
        border-top: 2px solid var(--bokbo-accent, #D63031);\
        box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);\
      }\
      .bokbo-result-img {\
        width: 48px;\
        height: 48px;\
      }\
    }\
  ';

  // ── Inject styles ────────────────────────────────────────────────
  function injectStyles() {
    var style = document.createElement('style');
    style.id = 'bokbo-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  // ── Debounce ─────────────────────────────────────────────────────
  function debounce(fn, delay) {
    var timer = null;
    var wrapped = function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        fn.apply(ctx, args);
      }, delay);
    };
    // Expose a .cancel() so callers can drop a pending run when
    // control flow changes — e.g. the shopper hits Enter before the
    // in-flight debounced search fires. Without this, Enter calls
    // hideDropdown() and then the pending timer re-renders + re-opens
    // the dropdown right after.
    wrapped.cancel = function () {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    return wrapped;
  }

  // ── Format price ─────────────────────────────────────────────────
  function formatPrice(price) {
    if (price == null || isNaN(price)) return '';
    return '$' + Number(price).toFixed(2);
  }

  // ── API call ─────────────────────────────────────────────────────
  var currentRequestId = 0;

  function searchApi(query, callback) {
    var requestId = ++currentRequestId;

    var xhr = new XMLHttpRequest();
    xhr.open('POST', BOKBO.apiUrl, true);
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4) return;

      // Ignore stale responses
      if (requestId !== currentRequestId) return;

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var data = JSON.parse(xhr.responseText);
          callback(null, data);
        } catch (e) {
          callback(e, null);
        }
      } else {
        callback(new Error('Search API ' + xhr.status), null);
      }
    };

    xhr.send(JSON.stringify({
      query: query,
      store_id: BOKBO.storeId,
      limit: BOKBO.resultLimit,
    }));
  }

  // ── Render dropdown content ──────────────────────────────────────
  var stillSearchingTimer = null;

  function renderSkeleton(dropdown) {
    var html = '';
    for (var i = 0; i < 6; i++) {
      html +=
        '<div class="bokbo-skeleton-row">' +
          '<div class="bokbo-skeleton-img"></div>' +
          '<div class="bokbo-skeleton-lines">' +
            '<div class="bokbo-skeleton-line bokbo-skeleton-line--title"></div>' +
            '<div class="bokbo-skeleton-line bokbo-skeleton-line--price"></div>' +
          '</div>' +
        '</div>';
    }
    html += '<div class="bokbo-still-searching" id="bokbo-still-searching" style="display:none;">Still searching\u2026</div>';
    dropdown.innerHTML = html;

    // Show "Still searching..." after 3 seconds
    clearTimeout(stillSearchingTimer);
    stillSearchingTimer = setTimeout(function () {
      var msg = document.getElementById('bokbo-still-searching');
      if (msg) msg.style.display = 'block';
    }, 3000);
  }

  function clearStillSearchingTimer() {
    clearTimeout(stillSearchingTimer);
    stillSearchingTimer = null;
  }

  function renderLoading(dropdown) {
    renderSkeleton(dropdown);
  }

  function renderNoResults(dropdown, query) {
    dropdown.innerHTML =
      '<div class="bokbo-no-results">No results for \u201C' +
      escapeHtml(query) +
      '\u201D</div>';
  }

  function renderResults(dropdown, results) {
    var html = '';

    for (var i = 0; i < results.length; i++) {
      var r = results[i];
      var imgSrc = r.image_url || '';
      var imgTag = imgSrc
        ? '<img class="bokbo-result-img" src="' + escapeHtml(imgSrc) + '" alt="" loading="lazy">'
        : '<div class="bokbo-result-img" style="display:flex;align-items:center;justify-content:center;color:#ccc;font-size:20px;">\u2697</div>';

      html +=
        '<a class="bokbo-result" href="' + escapeHtml(r.url || '#') + '" tabindex="0">' +
          imgTag +
          '<div class="bokbo-result-info">' +
            '<div class="bokbo-result-title">' + escapeHtml(r.title || '') + '</div>' +
            (r.product_type ? '<div class="bokbo-result-meta">' + escapeHtml(r.product_type) + '</div>' : '') +
          '</div>' +
          '<div class="bokbo-result-price">' + formatPrice(r.price) + '</div>' +
        '</a>';
    }

    if (BOKBO.showPoweredBy) {
      html += '<div class="bokbo-footer"><a href="https://bokbo.com" target="_blank" rel="noopener">Powered by BokBo</a></div>';
    }
    dropdown.innerHTML = html;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // ── Search-input selectors (priority order) ──────────────────────
  var SEARCH_SELECTORS = [
    '#Search-In-Modal',
    'input[type="search"]',
    'input[name="q"]',
    'input[name="search"]',
    'input[aria-label="Search"]',
    '.search-input input',
    '.search__input',
    '#search-input',
    '#Search',
    'form[action="/search"] input[type="text"]',
    'form[action="/search"] input[type="search"]',
  ];

  // Joined into one selector for matches() / closest() checks
  var SEARCH_SELECTOR_STR = SEARCH_SELECTORS.join(',');

  // ── Show / hide ──────────────────────────────────────────────────
  function showDropdown(dropdown) {
    dropdown.classList.add('bokbo-visible');
  }

  function hideDropdown(dropdown) {
    if (!dropdown) return;
    dropdown.classList.remove('bokbo-visible');
    // Always clear the searching pulse when the dropdown hides —
    // covers Escape, outside-click, min-chars bail, etc.
    if (activeInput) hideSearchingPulse(activeInput);
  }

  // Set true while we're redirecting via goToBokboSearch — any pending
  // debouncedSearch callback that fires during the transition is a
  // no-op while this is true. Cleared on the next user input event so
  // the shopper can search again from the results page if they want.
  var suppressDropdown = false;

  /**
   * Hard close: clear the searching pulse, hide the dropdown, cancel
   * any pending debounced search, and block re-opens until the shopper
   * types again. Used on Enter / form submit / search-link click, i.e.
   * every code path where we're about to navigate (or pushState-render
   * inline) and the dropdown must stay closed across that transition.
   */
  function closeDropdown() {
    suppressDropdown = true;
    if (typeof debouncedSearch !== 'undefined' && debouncedSearch && debouncedSearch.cancel) {
      debouncedSearch.cancel();
    }
    if (dropdown) hideDropdown(dropdown);
  }

  // ── Floating dropdown (shared, repositioned per-input) ──────────
  var dropdown = null;   // created once in init()
  var activeInput = null; // the input the dropdown is currently serving

  function ensureDropdown() {
    if (dropdown) return dropdown;
    dropdown = document.createElement('div');
    dropdown.className = 'bokbo-dropdown';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Search results');
    // Position absolutely relative to the viewport via fixed positioning
    dropdown.style.position = 'fixed';
    dropdown.style.zIndex = '999999';
    document.body.appendChild(dropdown);
    return dropdown;
  }

  /** Position the dropdown directly below the given input element. */
  function positionDropdown(input) {
    var rect = input.getBoundingClientRect();
    dropdown.style.top = rect.bottom + 'px';
    dropdown.style.left = rect.left + 'px';
    dropdown.style.width = rect.width + 'px';
  }

  // ── Keyboard navigation inside the dropdown ─────────────────────
  function handleDropdownKeydown(e) {
    if (!dropdown || !activeInput) return;
    var links = dropdown.querySelectorAll('.bokbo-result');
    if (!links.length) return;

    var focused = dropdown.querySelector('.bokbo-result:focus');
    var idx = -1;
    for (var i = 0; i < links.length; i++) {
      if (links[i] === focused) { idx = i; break; }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      var next = idx < links.length - 1 ? idx + 1 : 0;
      links[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx <= 0) {
        activeInput.focus();
      } else {
        links[idx - 1].focus();
      }
    } else if (e.key === 'Escape') {
      hideDropdown(dropdown);
      activeInput.focus();
    }
  }

  // ── Event delegation — the heart of the new approach ────────────
  //
  // Instead of binding listeners to each input (which breaks when the
  // input is destroyed / recreated, as RackHunt's modal does), we
  // listen on document.body and check whether the event target is one
  // of our recognised search inputs.  This survives DOM teardown.
  //
  // IMPORTANT: keydown is handled in a SEPARATE listener that ONLY deals
  // with special keys (Enter, Escape, ArrowDown).  It NEVER calls
  // debouncedSearch — because on keydown the character hasn't entered
  // the input yet, so input.value is stale.  Search is triggered by
  // keyup / input / change / paste instead.
  //

  /** Return the target element if it matches our search selectors. */
  function matchSearchInput(e) {
    var t = e.target;
    if (!t || t.nodeType !== 1) return null;
    // Direct match
    if (t.matches && t.matches(SEARCH_SELECTOR_STR)) return t;
    // Some themes wrap the real input — walk up one level
    if (t.closest) {
      var parent = t.closest(SEARCH_SELECTOR_STR);
      if (parent && (parent.tagName === 'INPUT' || parent.tagName === 'TEXTAREA')) return parent;
    }
    return null;
  }

  // Helpers to toggle the pulsing "searching" indicator on the active input.
  // Defined above debouncedSearch so they can be reused on error/cleanup paths.
  function showSearchingPulse(input) {
    try { if (input && input.classList) input.classList.add('bokbo-searching'); } catch (_) {}
  }
  function hideSearchingPulse(input) {
    try { if (input && input.classList) input.classList.remove('bokbo-searching'); } catch (_) {}
  }

  // Debounced search — fires the actual API call
  var debouncedSearch = debounce(function (input) {
    // Guard: if the input was destroyed since the debounce was queued,
    // bail out instead of reading a detached node.
    if (!input || !input.isConnected) {
      console.log('[BokBo] debounce fired but input is detached — skipping');
      hideSearchingPulse(input);
      return;
    }

    // If closeDropdown() set the suppress flag between "debounce
    // queued" and "debounce fired", stay quiet. The shopper hit Enter
    // (or submitted the form) during the debounce window and is mid-
    // navigation — reopening the dropdown now would paint right as the
    // results page loads.
    if (suppressDropdown) {
      hideSearchingPulse(input);
      return;
    }

    var query = input.value.trim();
    console.log('[BokBo] debounced search firing, value:', query);

    if (query.length < BOKBO.minChars) {
      hideDropdown(dropdown);
      hideSearchingPulse(input);
      return;
    }

    renderLoading(dropdown);
    positionDropdown(input);
    showDropdown(dropdown);
    showSearchingPulse(input);

    searchApi(query, function (err, data) {
      clearStillSearchingTimer();
      hideSearchingPulse(input);

      if (err) {
        console.error('[BokBo] Search error:', err);
        hideDropdown(dropdown);
        return;
      }

      var results = data && data.results ? data.results : [];

      if (results.length === 0) {
        renderNoResults(dropdown, query);
      } else {
        renderResults(dropdown, results);
      }

      positionDropdown(input);
      showDropdown(dropdown);
    });
  }, BOKBO.debounceMs);

  /**
   * Keydown handler — ONLY for special keys.
   * Never triggers debouncedSearch (value is stale on keydown).
   * Uses bubble phase so Shopify's own handlers run first.
   */
  function handleKeydown(e) {
    if (!BOKBO.searchEnabled) return;
    var input = matchSearchInput(e);
    if (!input) return;

    activeInput = input;
    ensureDropdown();

    // ArrowDown → move focus into dropdown results
    if (e.key === 'ArrowDown' && dropdown.classList.contains('bokbo-visible')) {
      e.preventDefault();
      var firstLink = dropdown.querySelector('.bokbo-result');
      if (firstLink) firstLink.focus();
      return;
    }

    // Escape → close dropdown
    if (e.key === 'Escape') {
      hideDropdown(dropdown);
      return;
    }

    // Enter → redirect to BokBo results page.
    //
    // We ALWAYS preventDefault here (not just when q >= minChars) because
    // the shopper's intent on Enter inside a BokBo-instrumented search
    // input is unambiguous: they want BokBo results, not whatever Shopify's
    // native /search would render. Silently eating too-short queries beats
    // falling through to the native page. Shorter-than-floor submissions
    // simply do nothing. No stopPropagation — let Shopify's modal JS still
    // receive the event so focus/state management isn't disturbed.
    if (e.key === 'Enter') {
      e.preventDefault();
      // closeDropdown() cancels any pending debouncedSearch AND sets
      // the suppress flag so an in-flight debounce can't reopen the
      // dropdown after goToBokboSearch takes the inline-render path
      // (pushState, no full navigation — listeners still bound).
      closeDropdown();
      goToBokboSearch(input.value.trim()); // no-ops when < REDIRECT_MIN_LEN
      return;
    }
  }

  // ── Navigation helper ───────────────────────────────────────────
  //
  // Send the user to the dedicated BokBo search page instead of Shopify's
  // native /search route. We also stash the query in sessionStorage under
  // `bokbo_query` so the results widget can pick it up synchronously — this
  // avoids a round-trip through the URL when the query is long / has chars
  // that would URL-encode awkwardly, and gives a reliable source-of-truth
  // even if a theme rewrites the querystring on its way to the page.
  //
  // Hard floor of 2 characters (REDIRECT_MIN_LEN) regardless of what the
  // shop admin has configured as BOKBO.minChars — a 1-character query is
  // too ambiguous to justify navigating away from the current page, and
  // silently bailing is better than falling through to Shopify's native
  // search (which would produce an inferior result set under our widget's
  // brand). Callers can check the return value to know whether navigation
  // was actually attempted.
  //
  // Uses location.replace() instead of location.href: the page the user
  // came from was already a search-results-capable page (they typed into
  // the header input). Pushing /pages/bokbo-search onto the history stack
  // would make the back button take them through the "briefly a 404"
  // intermediate frame before their original page, which the theme loads
  // fast enough to show a flash. location.replace() swaps the entry so
  // back goes directly to wherever they were searching from, and the
  // browser skips paint of the intermediate URL.
  var REDIRECT_MIN_LEN = 2;
  function goToBokboSearch(q) {
    var trimmed = (q || '').trim();
    if (trimmed.length < REDIRECT_MIN_LEN) return false;
    try {
      window.sessionStorage.setItem('bokbo_query', trimmed);
    } catch (_) {
      // Private browsing / storage quota / disabled — fall through to the
      // URL-only path, which still works.
    }

    // The shopper's results page lives at /pages/bokbo-search (Shopify
    // page handle = "bokbo-search", bound to page.bokbo-search.json →
    // bokbo-search-page.liquid in the theme). Keep this in lockstep
    // with the Shopify page handle the merchant published — renaming
    // the page without updating this URL lands shoppers on a 404.
    var targetUrl = '/pages/bokbo-search?q=' + encodeURIComponent(trimmed);

    // Give the BokBo results widget (loaded on the same page, if the
    // shopper is already inside /pages/bokbo-search or a legacy
    // /search page) a chance to re-render inline. Listener sets
    // event.detail.handled = true when it claims the navigation, so
    // we only pushState + skip the hard redirect when someone is
    // actually going to render the new query.
    var handled = false;
    try {
      var evt = new CustomEvent('bokbo:navigate', {
        detail: { query: trimmed, targetUrl: targetUrl, handled: false },
        cancelable: true,
      });
      document.dispatchEvent(evt);
      handled = !!(evt.defaultPrevented || (evt.detail && evt.detail.handled));
    } catch (_) {
      // Ancient browser without CustomEvent support — fall through.
    }

    if (handled) {
      // Results widget is handling render inline. Sync the URL so
      // bookmarks + back button reflect the current query, then
      // return without navigating.
      try {
        window.history.pushState({ bokboQuery: trimmed }, '', targetUrl);
      } catch (_) { /* no-op */ }
      return true;
    }

    // No inline handler — do the classical navigation. location.replace
    // (not assign) so the pre-search URL doesn't pile up in history —
    // the BokBo results page handles its own back-button story via
    // the popstate listener in bokbo-results.js.
    window.location.replace(targetUrl);
    return true;
  }

  /**
   * Search-trigger handler — fires on keyup, input, change, paste.
   * These events fire AFTER the character is in the input, so
   * input.value is always current.
   */
  function handleSearchTrigger(e) {
    if (!BOKBO.searchEnabled) return;
    var input = matchSearchInput(e);
    if (!input) return;

    console.log('[BokBo] search trigger:', e.type, '| target:', input.id || input.name || '(unnamed)', '| value:', input.value);

    // Any fresh user input releases the post-navigation suppress lock.
    // Without this, after Enter → closeDropdown() suppresses, the next
    // time the shopper types anywhere the dropdown would never reopen.
    if (suppressDropdown) suppressDropdown = false;

    activeInput = input;
    ensureDropdown();
    debouncedSearch(input);
  }

  // ── Init ─────────────────────────────────────────────────────────
  function init() {
    // Dropdown gate — merchants can disable the autocomplete dropdown
    // independently of disabling search entirely. Checks both the
    // BOKBO config (set by applySettings) and the synchronous proxy
    // global. When this fires we skip ONLY the dropdown/autocomplete
    // bindings below, but still install the header form-submit and
    // /search?q link interception so a native Shopify search from the
    // header still routes through /pages/bokbo-search instead of
    // falling through to the native results page.
    var skipDropdown = false;
    if (
      BOKBO.dropdownEnabled === false ||
      (window.__BOKBO_SETTINGS && window.__BOKBO_SETTINGS.dropdownEnabled === false)
    ) {
      console.log('[BokBo] Dropdown disabled via settings — skipping autocomplete, keeping form-submit interception.');
      skipDropdown = true;
    }

    // On the dedicated BokBo results page (/pages/bokbo-search) the
    // full results widget renders inline — the autocomplete dropdown
    // would paint on top of those results, which is visual noise at
    // best and a trap at worst. Skip the autocomplete bindings here
    // BUT KEEP the header form-submit + /search?q link interception:
    // a shopper typing a second query into the header search on this
    // page would otherwise fall through to native Shopify /search,
    // defeating the whole BokBo landing page.
    try {
      var pathname = (window.location && window.location.pathname) || '';
      if (/^\/pages\/bokbo-search\/?$/.test(pathname)) {
        console.log('[BokBo] On /pages/bokbo-search — skipping autocomplete dropdown, keeping form-submit interception so header re-searches stay on BokBo.');
        skipDropdown = true;
      }
    } catch (_) { /* no window / no pathname — proceed as usual */ }

    injectStyles();

    if (!skipDropdown) {
      ensureDropdown();

      // Mark the page so bokbo-results.js can detect the dropdown is active
      window.__bokboSearchActive = true;

      // ── Keydown: special keys only (bubble phase, not capture) ─────
      document.body.addEventListener('keydown', handleKeydown, false);

      // ── Search triggers: events where input.value is up-to-date ───
      //    Uses bubble phase so Shopify's own handlers run first.
      var searchTriggerEvents = ['keyup', 'input', 'change', 'paste'];
      searchTriggerEvents.forEach(function (evt) {
        document.body.addEventListener(evt, handleSearchTrigger, false);
      });

      // Re-show dropdown when a search input gains focus and already has a query
      document.body.addEventListener('focus', function (e) {
        var input = matchSearchInput(e);
        if (!input) return;
        activeInput = input;
        console.log('[BokBo] focus on search input:', input.id || input.name || '(unnamed)');
        if (dropdown.innerHTML && input.value.trim().length >= BOKBO.minChars) {
          positionDropdown(input);
          showDropdown(dropdown);
        }
      }, true); // capture needed — focus doesn't bubble

      // Arrow / escape navigation inside the dropdown itself
      dropdown.addEventListener('keydown', handleDropdownKeydown);

      // Clicking a dropdown result → navigate directly to product URL.
      // Hide the dropdown BEFORE the navigation so a delayed page load
      // (slow Shopify theme JS, product page cold cache) doesn't leave
      // the autocomplete sheet visible on top of the new page's content.
      var handleResultActivate = function (e, link) {
        if (!link || !link.href) return;
        if (e && e.preventDefault) e.preventDefault();
        hideDropdown(dropdown);
        console.log('[BokBo] result activated, navigating to:', link.href);
        window.location.href = link.href;
      };

      dropdown.addEventListener('click', function (e) {
        handleResultActivate(e, e.target.closest('.bokbo-result'));
      });

      // Mobile touchend fallback. On some mobile browsers (especially
      // when a tap lands on a freshly-injected anchor mid-animation)
      // the `click` event gets swallowed by touch interaction, so the
      // shopper taps a product and nothing happens. Listening for
      // touchend on the actual dropdown node gives us a reliable
      // second path. Not passive — we need to preventDefault so the
      // subsequent synthetic click doesn't double-navigate.
      dropdown.addEventListener('touchend', function (e) {
        var link = e.target && e.target.closest && e.target.closest('.bokbo-result');
        if (link && link.href) {
          handleResultActivate(e, link);
        }
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', function (e) {
        if (!dropdown) return;
        if (dropdown.contains(e.target)) return;
        // Check if click target IS the active search input (tolerates
        // destroyed+recreated inputs by matching selectors, not identity)
        var clickedInput = matchSearchInput(e);
        if (clickedInput) {
          activeInput = clickedInput; // update reference to new element
          return;
        }
        hideDropdown(dropdown);
      });

      // Mobile — mirror the click-outside handler as a touchstart
      // listener. On iOS Safari / Android Chrome, a tap outside the
      // dropdown often fires touchstart → scroll-related preventDefault
      // paths that never generate a `click` event. Relying on click
      // alone leaves the dropdown stuck on-screen until the next page
      // load. Passive because we never preventDefault here; the tap
      // should still behave normally on whatever it hit.
      document.addEventListener('touchstart', function (e) {
        if (!dropdown) return;
        if (!dropdown.classList.contains('bokbo-visible')) return;
        var t = e.target;
        if (!t || !t.closest) return;
        if (t.closest('.bokbo-dropdown')) return;        // tap inside dropdown
        if (t.closest('.bokbo-result')) return;          // tap on a result (result click-handler navigates)
        if (matchSearchInput(e)) return;                 // tap on a recognized search input
        hideDropdown(dropdown);
      }, { passive: true });

      // Close the dropdown when the PAGE scrolls. Attached to window
      // only — attaching to document in capture phase would also fire
      // on the dropdown's own internal scrolling (when results overflow
      // max-height), which would close the sheet while the shopper was
      // actively browsing. Window-scroll is the right signal: it
      // specifically means the document body scrolled.
      //
      // Paired with `overscroll-behavior: contain` on .bokbo-dropdown
      // (see STYLES) so a drag-past-end of the internal result list
      // on iOS doesn't spill into a window scroll and close the sheet.
      window.addEventListener('scroll', function () {
        if (dropdown && dropdown.classList.contains('bokbo-visible')) {
          hideDropdown(dropdown);
        }
      }, { passive: true });
    }

    // Intercept search form submissions → redirect to BokBo results page
    document.body.addEventListener('submit', function (e) {
      if (!BOKBO.searchEnabled) return;
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[action="/search"]')) return;

      // If a dropdown result is focused, go directly to that product
      if (dropdown && dropdown.classList.contains('bokbo-visible')) {
        var focused = dropdown.querySelector('.bokbo-result:focus');
        if (focused) {
          e.preventDefault();
          window.location.href = focused.href;
          return;
        }
      }

      // Otherwise redirect the form submit through our results page.
      //
      // Always preventDefault on a BokBo-matched search form — falling
      // through to Shopify's /search handler on a too-short query would
      // show the native results page with our widget inactive, which
      // looks broken. goToBokboSearch() itself silently no-ops when the
      // trimmed query is under REDIRECT_MIN_LEN.
      var formInput = form.querySelector('input[name="q"]') ||
                      form.querySelector('input[type="search"]') ||
                      form.querySelector('input[type="text"]');
      if (formInput) {
        e.preventDefault();
        closeDropdown();
        goToBokboSearch(formInput.value.trim());
      }
    }, false); // bubble phase — let Shopify handle first

    // Intercept any <a href="/search?q=..."> clicks elsewhere on the page
    // (theme-rendered "see all results" links, popular-search chips, etc.)
    // and route them through the BokBo search page instead so the shopper
    // never lands on Shopify's native results and sees the flash of the
    // wrong layout. Only handles plain left-clicks — middle-click / cmd+click
    // / already-prevented events are left alone so users can still open
    // native results in a new tab if they really want to.
    document.body.addEventListener('click', function (e) {
      if (!BOKBO.searchEnabled) return;
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;                 // left-click only
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest && e.target.closest('a[href]');
      if (!link) return;

      // Resolve the href against the current origin so we can test pathname
      // reliably whether it's absolute or relative ("/search?q=…" or
      // "https://shop.com/search?q=…").
      var url;
      try {
        url = new URL(link.getAttribute('href'), window.location.href);
      } catch (_) { return; }

      // Only touch same-origin /search links with a non-empty q param.
      if (url.origin !== window.location.origin) return;
      if (url.pathname !== '/search' && url.pathname !== '/search/') return;
      var q = (url.searchParams.get('q') || '').trim();
      if (!q) return;

      e.preventDefault();
      closeDropdown();
      goToBokboSearch(q);
    }, false);

    console.log('[BokBo] Widget initialized — using event delegation on document.body.');
  }

  // ── Boot ─────────────────────────────────────────────────────────
  function bootWithSettings() {
    // Set the CSS variable to the compile-time default immediately so
    // the dropdown styles render correctly even before settings arrive.
    document.documentElement.style.setProperty('--bokbo-accent', BOKBO.accentColor);

    // Fast path: if the app proxy <script src="/apps/bokbo/settings.js">
    // already ran, window.__BOKBO_SETTINGS is set synchronously — no
    // fetch needed, no timeout race.
    var proxySettings = window.__BOKBO_SETTINGS || null;
    if (proxySettings) {
      console.log('[BokBo] Using pre-loaded settings from app proxy');
      window[SETTINGS_CACHE_KEY] = { data: proxySettings, fetchedAt: Date.now() };
    }

    var onSettings = function (settings) {
      applySettings(settings);
      if (!BOKBO.searchEnabled) {
        console.log('[BokBo] Search disabled via settings — native Shopify search will handle queries.');
        return;
      }
      init();
    };

    if (proxySettings) {
      onSettings(proxySettings);
    } else {
      fetchSettings(onSettings);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootWithSettings);
  } else {
    bootWithSettings();
  }

})();
